import { inject } from '@angular/core';
import { ResolveFn } from '@angular/router';
import { extractErrorMessage, RecaptchaService } from '@crowdctrl/shared';
import {
  PublicFormContextResponse,
  PublicFormIntakeService,
} from '../services/public-form-intake.service';
import { PublicFormShortLinkService } from '../services/public-form-short-link.service';

export interface PublicFormContextResolvedData {
  shortUrlDocId: string;
  context: PublicFormContextResponse | null;
  errorMessage: string | null;
}

/**
 * Preloads context so the route can render with a stable, final title.
 * If loading fails, we still activate the page and let it show retry/error UI.
 */
export const publicFormContextResolver: ResolveFn<PublicFormContextResolvedData> = async (route) => {
  const recaptcha = inject(RecaptchaService);
  const intake = inject(PublicFormIntakeService);
  const shortLink = inject(PublicFormShortLinkService);

  const fromRoute = route.paramMap.get('shortId')?.trim() ?? '';
  const fromGuard = shortLink.shortUrlDocId?.trim() ?? '';
  const shortUrlDocId = fromRoute || fromGuard;

  if (!shortUrlDocId) {
    return {
      shortUrlDocId: '',
      context: null,
      errorMessage: 'Missing short link identifier.',
    };
  }

  if (!recaptcha.isConfigured()) {
    return {
      shortUrlDocId,
      context: null,
      errorMessage:
        'This form cannot be loaded. Check that the API URL and reCAPTCHA site key are configured.',
    };
  }

  try {
    const token = await recaptcha.execute('public_form_context');
    const context = await intake.fetchContext(shortUrlDocId, token);
    return {
      shortUrlDocId,
      context,
      errorMessage: null,
    };
  } catch (error) {
    return {
      shortUrlDocId,
      context: null,
      errorMessage: extractErrorMessage(error) || 'Failed to load form',
    };
  }
};
